import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, X } from "lucide-react";
import { useState } from "react";
import type { BookingCancellationReason } from "@apartos/contracts";
import { useDialog } from "../hooks/useDialog";
import { api } from "../lib/api";
import { cancellationReasonLabels } from "../lib/cancellation";
import { ModalPortal } from "./ModalPortal";

// Отмена брони с обязательной причиной. Бронь не удаляется: остаётся в «Бронированиях» и отчётах со статусом
// «Отменена» и причиной, но перестаёт занимать апартамент и не попадает в долги и кассу.
export function CancelBookingModal({ booking, onClose, onDone }: {
  booking: { id: string; guestName: string; apartmentNumber: string; paid?: number };
  onClose: () => void;
  onDone?: () => void;
}) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const [reason, setReason] = useState<BookingCancellationReason | null>(null);
  const [note, setNote] = useState("");
  const mutation = useMutation({
    mutationFn: () => api.cancelBooking(booking.id, { reason: reason!, note: note.trim() || undefined }),
    onSuccess: async () => {
      await Promise.all(["bookings", "apartments", "dashboard", "movements", "calendar-blocks"].map((key) => client.invalidateQueries({ queryKey: [key] })));
      onDone?.();
      onClose();
    },
  });
  const ready = Boolean(reason) && (reason !== "other" || note.trim().length > 0);
  return <ModalPortal onClose={onClose}>
    <section ref={dialogRef} className="booking-modal no-show-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-booking-title">
      <header className="modal-header"><div><h2 id="cancel-booking-title">Отменить бронь</h2><p>{booking.guestName} · Apt {booking.apartmentNumber}</p></div><button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть"><X size={17} /></button></header>
      <div className="modal-body">
        <div className="cancel-reasons" role="radiogroup" aria-label="Причина отмены">
          {(Object.keys(cancellationReasonLabels) as BookingCancellationReason[]).map((key) => <label key={key} className={reason === key ? "is-selected" : ""}>
            <input type="radio" name="cancel-reason" checked={reason === key} onChange={() => setReason(key)} />{cancellationReasonLabels[key]}
          </label>)}
        </div>
        <label className="field"><span>{reason === "other" ? "Причина (обязательно)" : "Комментарий"}</span><textarea rows={2} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} placeholder={reason === "moved" ? "Например: переселили в Apt 14" : ""} /></label>
        <div className="form-notice"><CircleAlert size={16} /><span>Бронь останется в истории и отчётах со статусом «Отменена» и этой причиной. Апартамент освободится, бронь не попадёт в долги и кассу. Если по брони уже принята оплата, сначала оформите возврат.</span></div>
        {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15} />{mutation.error.message}</div>}
      </div>
      <footer className="modal-footer"><button type="button" className="secondary-button" onClick={onClose}>Вернуться</button><button type="button" className="danger-button" disabled={!ready || mutation.isPending} onClick={() => mutation.mutate()}>{mutation.isPending ? "Отменяем…" : "Отменить бронь"}</button></footer>
    </section>
  </ModalPortal>;
}
